import React, { useEffect, useRef, useState } from 'react';
import {
  ArrowRight,
  Building2,
  CheckCircle2,
  Download,
  FileText,
  Link2,
  Loader2,
  Search,
  X,
} from 'lucide-react';

import apiService from '../../services/api';
import type {
  AnalysisProfile,
  CompanyReportCreated,
  TaseCompanySearchResult,
  TaseFiling,
  TaseLiveFiling,
} from '../../types';
import { filingPeriodFromCatalog } from '../../utils/reportDashboard';

interface ReportsImportDialogProps {
  open: boolean;
  initialCompanyName?: string;
  initialAnalysisProfile?: AnalysisProfile;
  onClose: () => void;
  onCreated: (report: CompanyReportCreated) => void;
}

const ReportsImportDialog: React.FC<ReportsImportDialogProps> = ({
  open,
  initialCompanyName = '',
  initialAnalysisProfile = 'general',
  onClose,
  onCreated,
}) => {
  const [query, setQuery] = useState(initialCompanyName);
  const [companies, setCompanies] = useState<TaseCompanySearchResult[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<TaseCompanySearchResult | null>(null);
  const [analysisProfile, setAnalysisProfile] = useState<AnalysisProfile>(initialAnalysisProfile);
  const [filings, setFilings] = useState<TaseLiveFiling[]>([]);
  const [loading, setLoading] = useState(false);
  const [filingsLoading, setFilingsLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [activeUrl, setActiveUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [doneUrl, setDoneUrl] = useState<string | null>(null);
  const [directOpen, setDirectOpen] = useState(false);
  const [directUrl, setDirectUrl] = useState('');
  const [directCompany, setDirectCompany] = useState(initialCompanyName);
  const [directYear, setDirectYear] = useState(new Date().getFullYear() - 1);
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    setQuery(initialCompanyName);
    setCompanies([]);
    setSelectedCompany(null);
    setAnalysisProfile(initialAnalysisProfile);
    setFilings([]);
    setDirectCompany(initialCompanyName);
    setError(null);
    setSearchError(null);
    setDoneUrl(null);
    setTimeout(() => searchRef.current?.focus(), 0);
  }, [open, initialAnalysisProfile, initialCompanyName]);

  useEffect(() => {
    if (!open || selectedCompany) return;
    const needle = query.trim();
    if (needle.length < 2) {
      setCompanies([]);
      setLoading(false);
      setSearchError(null);
      return;
    }
    setCompanies([]);
    setLoading(true);
    setSearchError(null);
    let cancelled = false;
    const timer = window.setTimeout(() => {
      apiService.searchTaseCompanies(needle)
        .then((results) => {
          if (!cancelled) setCompanies(results);
        })
        .catch(() => {
          if (!cancelled) setSearchError('החיפוש באתר מאיה נכשל. נסה שוב בעוד רגע.');
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }, 350);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [open, query, selectedCompany]);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !activeUrl) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, activeUrl, onClose]);

  if (!open) return null;

  const importAndRegister = async (
    filing: Pick<TaseFiling, 'url' | 'title' | 'year' | 'period'> &
      Partial<Pick<TaseLiveFiling, 'report_id' | 'company_id' | 'structured_available'>>,
    company: { name: string; sector?: string; tase_issuer_id?: string | null; analysis_profile?: AnalysisProfile },
  ) => {
    setActiveUrl(filing.url);
    setError(null);
    try {
      const imported = await apiService.importTaseFiling({
        url: filing.url,
        company: company.name,
        title: filing.title,
        report_id: filing.report_id,
        company_id: filing.company_id,
        prefer_structured: filing.structured_available === true,
      });
      const period = filingPeriodFromCatalog(filing);
      const report = await apiService.createCompanyReport({
        document_id: imported.document_id,
        company_name: company.name,
        tase_issuer_id: company.tase_issuer_id || undefined,
        sector: company.sector,
        analysis_profile: company.analysis_profile || analysisProfile,
        period_year: filing.year,
        period_quarter: period.period_quarter,
        report_type: period.report_type,
      });
      setDoneUrl(filing.url);
      onCreated(report);
    } catch (cause: unknown) {
      const detail = (cause as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setError(detail || 'ייבוא הדוח נכשל. ודא שהקישור תקין ונסה שוב.');
    } finally {
      setActiveUrl(null);
    }
  };

  const chooseCompany = async (company: TaseCompanySearchResult) => {
    setSelectedCompany(company);
    setAnalysisProfile(company.suggested_profile || 'general');
    setFilings([]);
    setFilingsLoading(true);
    setSearchError(null);
    try {
      setFilings(await apiService.getTaseCompanyFinancialReports(company.company_id));
    } catch {
      setSearchError('לא הצלחנו לטעון את הדוחות הכספיים של החברה ממאיה. נסה שוב.');
    } finally {
      setFilingsLoading(false);
    }
  };

  const chooseAnotherCompany = () => {
    setSelectedCompany(null);
    setFilings([]);
    setSearchError(null);
    setTimeout(() => searchRef.current?.focus(), 0);
  };

  const submitDirect = () => {
    if (!directUrl.trim() || !directCompany.trim() || !directYear) {
      setError('יש למלא קישור, שם חברה ושנת דוח.');
      return;
    }
    void importAndRegister({
      url: directUrl.trim(),
      title: `דוח תקופתי ${directYear}`,
      year: directYear,
      period: 'שנתי',
    }, { name: directCompany.trim(), analysis_profile: analysisProfile });
  };

  const formatPublishedAt = (value: string) => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    return new Intl.DateTimeFormat('he-IL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).format(date);
  };

  const renderAnalysisProfileSelector = (radioGroup: string) => (
    <fieldset>
      <legend className="text-xs font-semibold text-gray-700">מסלול ניתוח</legend>
      <div className="mt-2 grid gap-2 sm:grid-cols-2">
        {([
          {
            value: 'general' as const,
            title: 'חברה כללית',
            description: 'איתנות, תזרים, מבנה חוב וקובננטים פיננסיים.',
          },
          {
            value: 'real_estate' as const,
            title: 'בינוי ונדל״ן',
            description: 'קורפוס בנק ישראל הקיים בתוספת מינוף, נזילות ורגישויות.',
          },
        ]).map((option) => {
          const selected = analysisProfile === option.value;
          return (
            <label
              key={option.value}
              className={`cursor-pointer rounded-xl border p-3 transition-colors ${selected ? 'border-primary-400 bg-primary-50' : 'border-gray-200 bg-white hover:border-gray-300'}`}
            >
              <span className="flex items-start gap-2">
                <input
                  type="radio"
                  name={`analysis-profile-${radioGroup}`}
                  value={option.value}
                  checked={selected}
                  onChange={() => setAnalysisProfile(option.value)}
                  className="mt-1 h-4 w-4 border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span>
                  <span className="block text-sm font-semibold text-gray-900">{option.title}</span>
                  <span className="mt-0.5 block text-xs leading-5 text-gray-500">{option.description}</span>
                </span>
              </span>
            </label>
          );
        })}
      </div>
    </fieldset>
  );

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/55 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="reports-import-title"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !activeUrl) onClose();
      }}
    >
      <div className="flex max-h-[88vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
        <header className="flex items-start gap-3 border-b border-gray-200 px-5 py-4 sm:px-6">
          <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-primary-50 text-primary-700">
            <Building2 className="h-5 w-5" aria-hidden="true" />
          </span>
          <div className="min-w-0 flex-1">
            <h2 id="reports-import-title" className="text-base font-semibold text-gray-900">ייבוא דוח ציבורי</h2>
            <p className="mt-0.5 text-sm text-gray-500">חפש חברה באתר מאיה, בחר דוח כספי והוסף אותו למעקב ולעיבוד.</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={!!activeUrl}
            className="flex h-11 w-11 items-center justify-center rounded-lg text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900 disabled:opacity-40"
            aria-label="סגור"
          >
            <X className="h-5 w-5" />
          </button>
        </header>

        {!selectedCompany ? (
          <>
            <div className="border-b border-gray-100 px-5 py-4 sm:px-6">
              <label htmlFor="reports-company-search" className="mb-1.5 block text-sm font-medium text-gray-700">חיפוש חברה באתר מאיה</label>
              <div className="relative">
                <Search className="pointer-events-none absolute right-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <input
                  id="reports-company-search"
                  ref={searchRef}
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="שם חברה, למשל שיכון ובינוי"
                  autoComplete="off"
                  className="h-11 w-full rounded-xl border border-gray-300 bg-gray-50 pr-10 pl-3 text-sm outline-none transition focus:border-primary-400 focus:bg-white focus:ring-2 focus:ring-primary-500/15"
                />
              </div>
              <p className="mt-2 text-xs text-gray-500" aria-live="polite">
                {loading ? 'מחפש במאיה…' : query.trim().length < 2 ? 'יש להקליד לפחות שני תווים' : `${companies.length} חברות נמצאו`}
              </p>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4 sm:px-6">
              {loading ? (
                <div className="space-y-2" aria-label="מחפש חברות במאיה">
                  {[0, 1, 2].map((item) => <div key={item} className="h-16 animate-pulse rounded-xl bg-gray-100 motion-reduce:animate-none" />)}
                </div>
              ) : searchError ? (
                <div className="py-12 text-center" role="alert">
                  <Search className="mx-auto h-8 w-8 text-error-300" />
                  <p className="mt-3 text-sm font-medium text-error-700">{searchError}</p>
                </div>
              ) : query.trim().length < 2 ? (
                <div className="py-12 text-center">
                  <Building2 className="mx-auto h-8 w-8 text-gray-300" />
                  <p className="mt-3 text-sm font-medium text-gray-700">חפש חברה ציבורית</p>
                  <p className="mt-1 text-sm text-gray-500">התוצאות והדוחות ייטענו ישירות מאתר מאיה.</p>
                </div>
              ) : companies.length === 0 ? (
                <div className="py-12 text-center">
                  <Search className="mx-auto h-8 w-8 text-gray-300" />
                  <p className="mt-3 text-sm font-medium text-gray-700">לא נמצאה חברה במאיה</p>
                  <p className="mt-1 text-sm text-gray-500">נסה שם מקוצר או ייבוא באמצעות קישור ישיר.</p>
                </div>
              ) : (
                <ul className="divide-y divide-gray-100 overflow-hidden rounded-xl border border-gray-200">
                  {companies.map((company) => (
                    <li key={company.company_id}>
                      <button
                        type="button"
                        onClick={() => void chooseCompany(company)}
                        className="group flex min-h-16 w-full items-center gap-3 px-4 py-3 text-right transition-colors hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500"
                      >
                        <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-700">
                          <Building2 className="h-4 w-4" />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-semibold text-gray-900">{company.name}</span>
                          <span className="mt-0.5 block text-xs text-gray-500">
                            מזהה מאיה {company.company_id} · {company.company_type === 'COMPANY' ? 'חברה נסחרת' : 'חברה מדווחת'}
                          </span>
                        </span>
                        <ArrowRight className="h-4 w-4 rotate-180 text-gray-400 transition-transform group-hover:-translate-x-0.5 group-hover:text-primary-700" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </>
        ) : (
          <>
            <div className="flex items-center gap-3 border-b border-gray-100 bg-gray-50 px-5 py-4 sm:px-6">
              <button
                type="button"
                onClick={chooseAnotherCompany}
                disabled={!!activeUrl}
                className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-lg text-gray-600 hover:bg-white hover:text-primary-700 disabled:opacity-40"
                aria-label="בחירת חברה אחרת"
              >
                <ArrowRight className="h-5 w-5" />
              </button>
              <div className="min-w-0 flex-1">
                <div className="text-xs font-medium text-gray-500">החברה שנבחרה</div>
                <h3 className="mt-0.5 truncate text-sm font-semibold text-gray-950">{selectedCompany.name}</h3>
              </div>
              {!filingsLoading && <span className="text-xs text-gray-500">{filings.length} דוחות כספיים</span>}
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4 sm:px-6">
              <div className="mb-4 rounded-xl border border-gray-200 bg-gray-50/70 p-3">
                {renderAnalysisProfileSelector('company')}
              </div>
              {filingsLoading ? (
                <div className="space-y-3" aria-label="טוען דוחות כספיים ממאיה">
                  {[0, 1, 2].map((item) => <div key={item} className="h-20 animate-pulse rounded-xl bg-gray-100 motion-reduce:animate-none" />)}
                </div>
              ) : searchError ? (
                <div className="py-12 text-center" role="alert">
                  <FileText className="mx-auto h-8 w-8 text-error-300" />
                  <p className="mt-3 text-sm font-medium text-error-700">{searchError}</p>
                  <button type="button" onClick={() => void chooseCompany(selectedCompany)} className="mt-4 min-h-11 rounded-lg px-4 text-sm font-medium text-primary-700 hover:bg-primary-50">נסה שוב</button>
                </div>
              ) : filings.length === 0 ? (
                <div className="py-12 text-center">
                  <FileText className="mx-auto h-8 w-8 text-gray-300" />
                  <p className="mt-3 text-sm font-medium text-gray-700">לא נמצאו דוחות כספיים זמינים</p>
                  <p className="mt-1 text-sm text-gray-500">אפשר לייבא דוח באמצעות קישור ישיר ממאיה.</p>
                </div>
              ) : (
                <ul className="divide-y divide-gray-100 overflow-hidden rounded-xl border border-gray-200">
                  {filings.map((filing) => {
                    const busy = activeUrl === filing.url;
                    const done = doneUrl === filing.url;
                    return (
                      <li key={filing.report_id} className="flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-start gap-2">
                            <FileText className="mt-0.5 h-4 w-4 flex-shrink-0 text-gray-400" />
                            <span className="text-sm font-medium leading-5 text-gray-800">{filing.title}</span>
                            {filing.structured_available && (
                              <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700">
                                iXBRL · ללא OCR
                              </span>
                            )}
                          </div>
                          <p className="mt-1 pr-6 text-xs text-gray-500">
                            {filing.period} · {filing.year} · פורסם {formatPublishedAt(filing.published_at)}
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => void importAndRegister(filing, {
                            name: filing.company_name || selectedCompany.name,
                            tase_issuer_id: filing.tase_issuer_id,
                            analysis_profile: analysisProfile,
                          })}
                          disabled={!!activeUrl || done}
                          className="inline-flex min-h-11 items-center justify-center gap-2 rounded-lg bg-primary-600 px-4 text-sm font-medium text-white transition-colors hover:bg-primary-700 disabled:bg-gray-200 disabled:text-gray-500"
                        >
                          {done ? <CheckCircle2 className="h-4 w-4" /> : busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                          {done ? 'נוסף למעקב' : busy ? 'מייבא…' : 'ייבוא דוח'}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </>
        )}

        <footer className="border-t border-gray-200 bg-gray-50 px-5 py-4 sm:px-6">
          {error && <p className="mb-3 text-sm text-error-700" role="alert">{error}</p>}
          <button
            type="button"
            onClick={() => setDirectOpen((value) => !value)}
            className="flex min-h-11 items-center gap-2 rounded-lg px-2 text-sm font-medium text-gray-700 hover:bg-gray-100"
            aria-expanded={directOpen}
          >
            <Link2 className="h-4 w-4" />
            הדוח לא מופיע בתוצאות? ייבוא מקישור ישיר
          </button>
          {directOpen && (
            <div className="mt-3 grid gap-3 rounded-xl border border-gray-200 bg-white p-4 sm:grid-cols-[1fr_140px]">
              <div className="sm:col-span-2">
                {renderAnalysisProfileSelector('direct')}
              </div>
              <label className="sm:col-span-2">
                <span className="mb-1 block text-xs font-medium text-gray-600">קישור לעמוד הדוח או PDF ממאיה</span>
                <input
                  value={directUrl}
                  onChange={(event) => setDirectUrl(event.target.value)}
                  dir="ltr"
                  className="h-11 w-full rounded-lg border border-gray-300 px-3 text-sm outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-500/15"
                  placeholder="https://maya.tase.co.il/he/reports/…"
                />
              </label>
              <label>
                <span className="mb-1 block text-xs font-medium text-gray-600">שם החברה</span>
                <input
                  value={directCompany}
                  onChange={(event) => setDirectCompany(event.target.value)}
                  className="h-11 w-full rounded-lg border border-gray-300 px-3 text-sm outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-500/15"
                />
              </label>
              <label>
                <span className="mb-1 block text-xs font-medium text-gray-600">שנה</span>
                <input
                  type="number"
                  min={1990}
                  max={2100}
                  value={directYear}
                  onChange={(event) => setDirectYear(Number(event.target.value))}
                  className="h-11 w-full rounded-lg border border-gray-300 px-3 text-sm outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-500/15"
                />
              </label>
              <button
                type="button"
                onClick={submitDirect}
                disabled={!!activeUrl}
                className="inline-flex min-h-11 items-center justify-center gap-2 rounded-lg bg-gray-900 px-4 text-sm font-medium text-white hover:bg-gray-800 disabled:opacity-40 sm:col-span-2 sm:justify-self-start"
              >
                {activeUrl === directUrl.trim() ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                ייבוא והוספה למעקב
              </button>
            </div>
          )}
        </footer>
      </div>
    </div>
  );
};

export default ReportsImportDialog;
